import iconArrowSquareOut from '@phosphor-icons/core/regular/arrow-square-out.svg';
import React, { useState } from 'react';
import { defineMessages, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import List, { ListItem } from '@/components/list';
import { CardTitle } from '@/components/ui/card';
import Column from '@/components/ui/column';
import Icon from '@/components/ui/icon';
import { useFeatures } from '@/hooks/use-features';
import { useOwnAccount } from '@/hooks/use-own-account';
import { usePrivileges } from '@/hooks/use-privileges';
import { Counter } from '@/pages/dashboard/components/counter';
import { DashCounter, DashCounters } from '@/pages/dashboard/components/dashcounter';
import { Dimension } from '@/pages/dashboard/components/dimension';
import RegistrationModePicker from '@/pages/dashboard/components/registration-mode-picker';
import { Retention } from '@/pages/dashboard/components/retention';
import { usePendingUsersCount } from '@/queries/admin/use-accounts';
import { useAdminConfig } from '@/queries/admin/use-config';
import { usePendingReportsCount } from '@/queries/admin/use-reports';
import { useInstance } from '@/stores/instance';
import sourceCode from '@/utils/code';

const messages = defineMessages({
  heading: { id: 'column.admin.dashboard', defaultMessage: 'Dashboard' },
});

interface IDashboardPage {
  aside?: boolean;
}

/** Return an ISO date a fixed number of days before another ISO date. */
const subtractDays = (date: string, days: number): string => {
  const result = new Date(`${date}T00:00:00Z`);
  result.setUTCDate(result.getUTCDate() - days);
  return result.toISOString().slice(0, 10);
};

const DashboardPage: React.FC<IDashboardPage> = ({ aside = false }) => {
  const intl = useIntl();
  const instance = useInstance();
  const features = useFeatures();
  const { data: account } = useOwnAccount();
  const { error } = useAdminConfig();
  const { privileges, hasPrivilege } = usePrivileges();

  const { data: awaitingApprovalCount = 0 } = usePendingUsersCount();
  const { data: pendingReportsCount = 0 } = usePendingReportsCount();

  const v = features.version;

  const {
    user_count: userCount,
    status_count: statusCount,
    domain_count: domainCount,
  } = instance.stats;

  const mau = instance.usage.users.active_month;
  const retention = userCount && mau ? Math.round((mau / userCount) * 100) : undefined;

  const [today] = useState<string>(new Date().toISOString().slice(0, 10));
  const monthAgo = subtractDays(today, 30);
  const sixMonthsAgo = subtractDays(today, 30 * 6);

  if (!account) return null;

  const configurationDisabled = (error as any)?.response?.json?.error?.includes(
    'configurable_from_database',
  );

  const canAdmin = !privileges || !!account.is_admin;

  return (
    <Column label={intl.formatMessage(messages.heading)}>
      <div className='dashboard'>
        {!aside && (
          <DashCounters>
            {features.mastodonAdminMetrics ? (
              <Counter
                measure='new_users'
                startAt={monthAgo}
                endAt={today}
                to='/nicolium/admin/accounts'
                search={{ origin: 'local' }}
                label={
                  <FormattedMessage id='admin.counters.new_users' defaultMessage='new users' />
                }
              />
            ) : (
              <DashCounter
                to='/nicolium/admin/accounts'
                search={{ origin: 'local' }}
                count={userCount}
                label={
                  <FormattedMessage
                    id='admin.dashcounters.user_count.label'
                    defaultMessage='total users'
                  />
                }
              />
            )}
            {features.mastodonAdminMetrics ? (
              <Counter
                measure='active_users'
                startAt={monthAgo}
                endAt={today}
                label={
                  <FormattedMessage
                    id='admin.counters.active_users'
                    defaultMessage='active users'
                  />
                }
              />
            ) : (
              <DashCounter
                count={mau}
                label={
                  <FormattedMessage
                    id='admin.dashcounters.mau.label'
                    defaultMessage='monthly active users'
                  />
                }
              />
            )}
            {!features.mastodonAdminMetrics && (
              <DashCounter
                count={retention}
                label={
                  <FormattedMessage
                    id='admin.dashcounters.retention.label'
                    defaultMessage='user retention'
                  />
                }
                percent
              />
            )}
            {features.mastodonAdminMetrics && (
              <>
                <Counter
                  measure='interactions'
                  startAt={monthAgo}
                  endAt={today}
                  label={
                    <FormattedMessage
                      id='admin.counters.interactions'
                      defaultMessage='interactions'
                    />
                  }
                />
                <Counter
                  measure='opened_reports'
                  startAt={monthAgo}
                  endAt={today}
                  to='/nicolium/admin/reports'
                  search={{ resolved: false }}
                  label={
                    <FormattedMessage
                      id='admin.counters.opened_reports'
                      defaultMessage='reports opened'
                    />
                  }
                />
                <Counter
                  measure='resolved_reports'
                  startAt={monthAgo}
                  endAt={today}
                  to='/nicolium/admin/reports'
                  search={{ resolved: true }}
                  label={
                    <FormattedMessage
                      id='admin.counters.resolved_reports'
                      defaultMessage='reports resolved'
                    />
                  }
                />
              </>
            )}
            <DashCounter
              to='/timeline/local'
              count={statusCount}
              label={
                <FormattedMessage
                  id='admin.dashcounters.status_count.label'
                  defaultMessage='posts'
                />
              }
            />
            <DashCounter
              count={domainCount}
              label={
                <FormattedMessage
                  id='admin.dashcounters.domain_count.label'
                  defaultMessage='peers'
                />
              }
            />
            {features.mastodonAdminMetrics && (
              <>
                <Dimension
                  dimension='sources'
                  startAt={monthAgo}
                  endAt={today}
                  params={{ limit: 8 }}
                  label={
                    <FormattedMessage
                      id='admin.dimensions.sources'
                      defaultMessage='Sign-up sources'
                    />
                  }
                />
                <Dimension
                  dimension='languages'
                  startAt={monthAgo}
                  endAt={today}
                  params={{ limit: 8 }}
                  label={
                    <FormattedMessage
                      id='admin.dimensions.top_languages'
                      defaultMessage='Top active languages'
                    />
                  }
                />
                <Dimension
                  dimension='servers'
                  startAt={monthAgo}
                  endAt={today}
                  params={{ limit: 8 }}
                  label={
                    <FormattedMessage
                      id='admin.dimensions.top_servers'
                      defaultMessage='Top active servers'
                    />
                  }
                />
                <Retention startAt={sixMonthsAgo} endAt={today} frequency='month' />
                <Dimension
                  dimension='software_versions'
                  startAt={monthAgo}
                  endAt={today}
                  params={{ limit: 4 }}
                  label={
                    <FormattedMessage id='admin.dimensions.software' defaultMessage='Software' />
                  }
                />
                <Dimension
                  dimension='space_usage'
                  startAt={monthAgo}
                  endAt={today}
                  params={{ limit: 3 }}
                  label={
                    <FormattedMessage
                      id='admin.dimensions.media_storage'
                      defaultMessage='Media storage'
                    />
                  }
                />
              </>
            )}
          </DashCounters>
        )}

        {!aside && (
          <DashCounters>
            {hasPrivilege('reports_manage_reports') && (
              <List>
                <ListItem
                  size='sm'
                  to='/nicolium/admin/reports'
                  search={{ resolved: false }}
                  label={
                    <FormattedMessage
                      id='admin.links.pending_reports'
                      defaultMessage='{count, plural, one {{formattedCount} pending report} other {{formattedCount} pending reports}}'
                      values={{
                        count: pendingReportsCount,
                        formattedCount: (
                          <strong>
                            <FormattedNumber value={pendingReportsCount} />
                          </strong>
                        ),
                      }}
                    />
                  }
                />
              </List>
            )}
            {hasPrivilege('users_read') && (
              <List>
                <ListItem
                  size='sm'
                  to='/nicolium/admin/approval'
                  label={
                    <FormattedMessage
                      id='admin.links.pending_users'
                      defaultMessage='{count, plural, one {{formattedCount} pending user} other {{formattedCount} pending users}}'
                      values={{
                        count: awaitingApprovalCount,
                        formattedCount: (
                          <strong>
                            <FormattedNumber value={awaitingApprovalCount} />
                          </strong>
                        ),
                      }}
                    />
                  }
                />
              </List>
            )}
          </DashCounters>
        )}

        <List>
          {account.is_admin && (
            <ListItem
              to='/nicolium/config'
              label={
                <FormattedMessage
                  id='column.frontend_config'
                  defaultMessage='Front-end configuration'
                />
              }
            />
          )}

          {(features.pleromaAdminAccounts || features.mastodonAdminV2) &&
            hasPrivilege('users_read') && (
              <ListItem
                to='/nicolium/admin/accounts'
                label={<FormattedMessage id='column.admin.accounts' defaultMessage='Accounts' />}
              />
            )}

          {(features.pleromaAdminAccounts || features.mastodonAdminV2 || features.iceshrimpAdmin) &&
            hasPrivilege('reports_manage_reports') && (
              <ListItem
                to='/nicolium/admin/reports'
                search={{ resolved: false }}
                label={<FormattedMessage id='column.admin.reports' defaultMessage='Reports' />}
              />
            )}

          {features.pleromaAdminModerationLog && hasPrivilege('moderation_log_read') && (
            <ListItem
              to='/nicolium/admin/log'
              label={
                <FormattedMessage
                  id='column.admin.moderation_log'
                  defaultMessage='Moderation log'
                />
              }
            />
          )}

          {(features.adminDomainBlocks || features.iceshrimpAdmin) &&
            canAdmin &&
            (instance.configuration.limited_federation ? (
              <ListItem
                to='/nicolium/admin/domain_allows'
                label={
                  <FormattedMessage
                    id='column.admin.domain_allows'
                    defaultMessage='Domain allows'
                  />
                }
              />
            ) : (
              <ListItem
                to='/nicolium/admin/domain_blocks'
                label={
                  <FormattedMessage
                    id='column.admin.domain_blocks'
                    defaultMessage='Domain blocks'
                  />
                }
              />
            ))}

          {features.adminEmailDomainBlocks && (
            <ListItem
              to='/nicolium/admin/email_domain_blocks'
              label={
                <FormattedMessage
                  id='column.admin.email_domain_blocks'
                  defaultMessage='E-mail domain blocks'
                />
              }
            />
          )}

          {features.adminIpBlocks && (
            <ListItem
              to='/nicolium/admin/ip_blocks'
              label={<FormattedMessage id='column.admin.ip_blocks' defaultMessage='IP blocks' />}
            />
          )}

          {features.adminCanonicalEmailBlocks && (
            <ListItem
              to='/nicolium/admin/canonical_email_blocks'
              label={
                <FormattedMessage
                  id='column.admin.canonical_email_blocks'
                  defaultMessage='Canonical e-mail blocks'
                />
              }
            />
          )}

          {features.pleromaAdminAnnouncements &&
            hasPrivilege('announcements_manage_announcements') && (
              <ListItem
                to='/nicolium/admin/announcements'
                label={
                  <FormattedMessage
                    id='column.admin.announcements'
                    defaultMessage='Announcements'
                  />
                }
              />
            )}

          {features.adminRules && canAdmin && (
            <ListItem
              to='/nicolium/admin/rules'
              label={<FormattedMessage id='column.admin.rules' defaultMessage='Instance rules' />}
            />
          )}

          {features.pleromaAdminConfig && canAdmin && (
            <ListItem
              to='/nicolium/admin/pleroma_config'
              label={
                <FormattedMessage
                  id='column.admin.pleroma_config'
                  defaultMessage='Pleroma configuration'
                />
              }
              disabled={configurationDisabled}
            />
          )}

          {(features.pleromaAdminRelays || features.iceshrimpAdmin) && canAdmin && (
            <ListItem
              to='/nicolium/admin/relays'
              label={<FormattedMessage id='column.admin.relays' defaultMessage='Instance relays' />}
            />
          )}

          {features.domains && canAdmin && (
            <ListItem
              to='/nicolium/admin/domains'
              label={<FormattedMessage id='column.admin.domains' defaultMessage='Domains' />}
            />
          )}

          {((features.pleromaAdminInvites && hasPrivilege('users_manage_invites')) ||
            features.iceshrimpAdmin) && (
            <ListItem
              to='/nicolium/admin/invites'
              label={<FormattedMessage id='column.admin.invites' defaultMessage='Invites' />}
            />
          )}

          {features.iceshrimpAdmin && (
            <ListItem
              to='/nicolium/admin/iceshrimp_policies'
              label={
                <FormattedMessage
                  id='column.admin.iceshrimp_policies'
                  defaultMessage='Iceshrimp.NET policies'
                />
              }
            />
          )}
        </List>

        {!aside && (
          <>
            {features.pleromaAdminAccounts && account.is_admin && (
              <>
                <CardTitle
                  title={
                    <FormattedMessage
                      id='admin.dashboard.registration_mode.label'
                      defaultMessage='Registrations'
                    />
                  }
                />

                <RegistrationModePicker />
              </>
            )}

            <CardTitle
              title={
                <FormattedMessage
                  id='admin.dashwidgets.software.header'
                  defaultMessage='Software'
                />
              }
            />

            <List>
              <ListItem
                label={<FormattedMessage id='admin.software.frontend' defaultMessage='Frontend' />}
              >
                <a
                  href={
                    sourceCode.ref ? `${sourceCode.url}/commit/${sourceCode.ref}` : sourceCode.url
                  }
                  className='dashboard__source-code'
                  target='_blank'
                >
                  <span>
                    {sourceCode.displayName} {sourceCode.version}
                  </span>

                  <Icon src={iconArrowSquareOut} />
                </a>
              </ListItem>

              {!features.mastodonAdminMetrics && (
                <ListItem
                  label={<FormattedMessage id='admin.software.backend' defaultMessage='Backend' />}
                >
                  <span>
                    {v.software} ({instance.version})
                  </span>
                </ListItem>
              )}
            </List>
          </>
        )}
      </div>
    </Column>
  );
};

export { DashboardPage as default };
